/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next v17 requires TypeScript 5+; this project is on TS 4.9.5.
// See src/i18n/index.ts for the project-wide workaround.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import InputField from '../../components/InputField/InputField';
import Button from '../../components/Button/Button';
import CountrySelect from '../../components/CountrySelect/CountrySelect';
import PhoneSegmented from '../../components/PhoneSegmented/PhoneSegmented';
import EditPrefRow from '../../components/EditPrefRow/EditPrefRow';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { isAuthV2Enabled } from '../../lib/auth/authV2Flag';
import { apiService } from '../../services/api';
import { normalizeE164, splitE164ForBackend } from '../../lib/validation';
import { getPasswordStrength, strengthToColor } from '../../lib/passwordStrength';
import { ProfileType } from '../../types/enums';
import {
    findCountry,
    getDefaultCountry,
    type SupportedCountryIso,
} from '../../lib/countries.seed';
import {
    signupStep1Schema,
    signupStep2Schema,
    signupStep3Schema,
    type SignupStep1Form,
    type SignupStep2Form,
    type SignupStep3Form,
} from '../../schemas/auth.schema';
import styles from './SignupForm.module.css';

const useTranslation = useTranslationUntyped as any;

// CR-2026-05-07a Phase 3 — added 'email' as the new first step (Fresha-style
// email-first entry + SSO providers). 'identifier' (country/phone/name)
// becomes Step 2. OTP + password unchanged.
// Identifier model is NOT changing — phone stays primary per AB#1576.
type SignupStep = 'email' | 'identifier' | 'otp' | 'password';

interface SignupFormProps {
    /** Callback to navigate back to the login form. */
    toggleForm: (form: 'login' | 'forgetPassword' | 'partner' | 'signup') => void;
}

/**
 * 3-step salon-owner self-registration form (AB#1568).
 *
 * Step 1 (identifier): country + phone + full name + email + terms
 *                      → POST /v2/auth/otp/request (channel='sms', purpose='signup')
 * Step 2 (otp):        4-6 digit OTP → POST /v2/auth/otp/verify
 *                      → carry the returned signup_scoped Bearer in memory
 * Step 3 (password):   password + confirm (email already captured at Step 1)
 *                      → POST /v1/auth/register (Authorization: Bearer <signup_scoped>,
 *                                                UserType=SalonOwner)
 *                      → POST /v1/auth/login → Bearer + Refresh
 *                      → setTokens → navigate /onboarding
 *
 * Why three steps:
 *   - OTP verifies the phone (per AB#1513 SMS-first thesis for MENA market).
 *   - Email + password is the ongoing auth method (recovery, multi-device, etc.).
 *   - The signup_scoped Bearer is the *attestation* the BE needs to mark the
 *     credential phone-verified at register time and skip the email-link
 *     verification gate that would otherwise block the user from logging in.
 *     See `Devanza.Infrastructure/Services/SignupAttestationValidator.cs`.
 *
 * History:
 *   - AB#1453 / AB#1451 / PO-D-012 — original signup OTP flow (PR #325, incomplete).
 *   - AB#1513 / PR #405 — fixed SMS channel.
 *   - AB#1559 / PR #456 — fixed FE→BE purpose enum mismatch.
 *   - AB#1560 / PR #459 — fixed OTP code length (4-6 to support bypass).
 *   - AB#1568 — added Step 3 + register + login wiring.
 *   - AB#1572 (deferred) — PDPL-grade DocVersionAcknowledged consent capture.
 *   - AB#1630 (this) — collapsed duplicate email field. Email moved from
 *     "optional Step 1 + required Step 3" to "required Step 1 only";
 *     register call reads from step1Values. Step 3 is now password-only.
 *
 * Country list comes from a TEMPORARY stub (`COUNTRY_SEED`); AB#1518 replaces
 * it with `/api/v2/countries/active`. Inactive countries (e.g. KSA) are visible
 * but block submission with a "Coming soon — waitlist" banner (no Step 2/3).
 *
 * Only rendered when REACT_APP_AUTH_V2_ENABLED=true.
 */
const SignupForm: React.FC<SignupFormProps> = ({ toggleForm }) => {
    const { t, i18n } = useTranslation('auth');
    const { setTokens, setEmailVerificationRequired, setPendingVerificationEmail } = useAuthContext();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const locale: 'en' | 'ar' = (i18n?.language || 'en').toString().toLowerCase().startsWith('ar')
        ? 'ar'
        : 'en';

    // CR-2026-05-07a Phase 2B — Time zone preference. Defaulted from selected
    // country (UAE → Asia/Dubai, JO → Asia/Amman). Stored in component state
    // for now; Phase 3 will persist via /v1/auth/register payload + a new
    // UserPreferences entity on the BE.
    const [tzPref, setTzPref] = useState<string>('Asia/Dubai');

    // Instant-apply language change (no Save button per founder rev 5).
    // Calls i18n.changeLanguage which the LanguageContext propagates app-wide
    // (RTL flip, t() re-evaluation). Persistence to user profile happens in
    // Phase 3 (Settings → Preferences screen reuses this same component).
    const onLanguageChange = (next: 'en' | 'ar') => {
        if (i18n?.changeLanguage) {
            i18n.changeLanguage(next);
        }
    };

    // ── Step state ──────────────────────────────────────────────────────────
    // Phase 3 — start at the new email-entry step. Old behaviour:
    // start at 'identifier'. The identifier step now skips its own email
    // field once entered; email is carried forward via emailStepValue.
    const [step, setStep] = useState<SignupStep>('email');
    const [emailStepValue, setEmailStepValue] = useState<string>('');
    // Phase 3 — INVISIBLE reCAPTCHA (Fresha-parity). No widget UI; the
    // captcha runs in the background when the user submits. Token is
    // obtained via ref.executeAsync(). Google requires either the badge
    // OR the disclaimer text be visible — we use the disclaimer pattern
    // (already shipped in the passive disclosure footer) and hide the
    // floating badge via CSS, per Google reCAPTCHA branding guidelines.
    //
    // Site key from REACT_APP_RECAPTCHA_SITE_KEY env var. Default falls
    // back to Google's PUBLIC TEST KEY (dev-only; BE rejects in prod
    // unless the real key is set).
    const recaptchaRef = React.useRef<ReCAPTCHA | null>(null);
    const recaptchaSiteKey =
        process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
        '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | undefined>(undefined);

    // CR-2026-05-06 — browser back/forward across signup steps. Without
    // this, browser-back from Step 2 (OTP) leaves /form/signup entirely
    // (typically to /form/login if that's where the user came from), which
    // is confusing UAT behaviour. We push a history entry per non-initial
    // step and listen to popstate to walk back through the step state.
    //
    // Implementation: each transition into 'otp' / 'password' calls
    // `pushState({signupStep})`. A popstate handler interprets the popped
    // state and rewinds setStep accordingly. Step 1 ('identifier') is the
    // route's "natural" state; popping past it lets React Router take over
    // and exit /form/signup as expected.
    useEffect(() => {
        // CR-2026-05-07a Phase 3 — replace the existing /form/signup
        // history entry with one explicitly tagged as the email step.
        // Without this, leftover state from previous sessions (e.g. an
        // entry tagged signupStep='otp' from an interrupted prior visit)
        // would deceive the popstate handler on first back-press.
        if (!window.history.state || !window.history.state.signupStep) {
            window.history.replaceState(
                { signupStep: 'email' },
                '',
                window.location.pathname,
            );
        }

        const onPop = (e: PopStateEvent) => {
            // CR-2026-05-07a Phase 3 — popping further back (no signupStep
            // on the popped state) means the user wants to leave /form/signup
            // entirely; React Router takes over. Otherwise, the popped state
            // tells us which step the user is heading back to.
            const target = (e.state && e.state.signupStep) as SignupStep | undefined;
            if (target === 'otp') {
                setStep('otp');
            } else if (target === 'password') {
                setStep('password');
            } else if (target === 'identifier') {
                setStep('identifier');
            } else {
                // Default fallback: pop to email (the new initial step).
                setStep('email');
            }
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // Carry data forward from step 1 → step 2 → step 3.
    // `normalizedIdentifier` is the E.164 wire format; `step1Values` keeps the
    // raw form snapshot so going back to edit shows what the user typed.
    const [step1Values, setStep1Values] = useState<SignupStep1Form | null>(null);
    const [normalizedIdentifier, setNormalizedIdentifier] = useState<string>('');
    const [requestId, setRequestId] = useState<string>('');
    // AB#1568 — the signup_scoped Bearer returned by /otp/verify on success.
    // Held in component state (NOT localStorage / sessionStorage) so a page
    // refresh forces a fresh OTP. ~10 minute TTL on the BE side; we surface
    // a "verification expired" error in the Step 3 submit path if the BE
    // rejects it.
    const [attestationToken, setAttestationToken] = useState<string>('');

    // CR-2026-05-07a Phase 3E (rev 2) — inline OTP + password in identifier
    // step. Founder UAT feedback: OTP belongs immediately below the mobile
    // input, password fields visible from the start (not gated on OTP),
    // and a checkmark indicator next to the mobile after verification.
    const [otpCardOpen, setOtpCardOpen] = useState<boolean>(false);
    const [phoneVerified, setPhoneVerified] = useState<boolean>(false);
    const [inlineOtpDigits, setInlineOtpDigits] = useState<string[]>(['', '', '', '']);
    const [inlineOtpError, setInlineOtpError] = useState<string | undefined>(undefined);
    const inlineOtpRefs = React.useRef<Array<HTMLInputElement | null>>([null, null, null, null]);

    // CR-2026-05-07a Phase 3E rev 6 / AB#1667 — Cache of attestation tokens
    // by E.164 phone. Survives country toggles inside the same signup
    // session so the user never has to re-verify a phone they already
    // verified once. Fixes the UAE → JO → UAE round-trip bug where the
    // BE rate-limits or rejects a second otpRequest for the same number.
    const verifiedPhonesRef = React.useRef<Map<string, string>>(new Map());

    // Resend countdown (seconds)
    const [resendIn, setResendIn] = useState(0);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendIn]);

    // ── Step 1 form ─────────────────────────────────────────────────────────
    const defaultCountry = useMemo<SupportedCountryIso>(() => getDefaultCountry().iso2, []);

    const {
        control: ctrl1,
        handleSubmit: submit1,
        watch: watch1,
        setValue: setValue1,
        formState: { errors: err1 },
    } = useForm<SignupStep1Form>({
        resolver: zodResolver(signupStep1Schema) as any,
        defaultValues: {
            country: defaultCountry,
            identifier: '',
            fullName: '',
            email: '',
        },
    });

    // Phase 3 — keep step1's email Controller in sync with the new email-
    // entry step. When user submits the email step, emailStepValue is set;
    // this effect mirrors it into the RHF form so existing schema validation
    // and the /v1/auth/register payload pickup paths are unchanged.
    useEffect(() => {
        if (emailStepValue) {
            setValue1('email', emailStepValue, { shouldValidate: false });
        }
    }, [emailStepValue, setValue1]);

    const watchedCountry = watch1('country');
    const watchedIdentifier = watch1('identifier');
    const selectedCountry = findCountry(watchedCountry);

    // AB#1667 + AB#1668 — phone-edit side effects.
    //
    // AB#1667: When the user types a phone we've already verified earlier
    // in this session (e.g. they country-toggled JO and came back to UAE
    // with the same number), restore phoneVerified + attestationToken from
    // the cache. Avoids the BE rate-limit on duplicate otpRequest.
    //
    // AB#1668: When the user EDITS the phone field while either the OTP
    // card is open OR the previous phone was verified, the prior state is
    // stale. Auto-close the card, reset OTP digits / requestId / resend
    // timer, and clear phoneVerified + attestationToken. Without this,
    // a typed code would be sent against a stale requestId from a
    // different number — confusing to the user, hard error from the BE.
    useEffect(() => {
        if (!watchedIdentifier || !watchedCountry) return;
        const wire = normalizeE164(watchedIdentifier, watchedCountry);
        if (!wire) return;

        const cachedAttestation = verifiedPhonesRef.current.get(wire);
        const phoneChanged = wire !== normalizedIdentifier;

        if (cachedAttestation) {
            // Same phone OR a previously-verified phone — restore /
            // confirm verified state without hitting the BE.
            if (!phoneVerified || phoneChanged) {
                setNormalizedIdentifier(wire);
                setAttestationToken(cachedAttestation);
                setPhoneVerified(true);
                setOtpCardOpen(false);
                setRequestId('');
                setInlineOtpDigits(['', '', '', '']);
                setInlineOtpError(undefined);
                setResendIn(0);
            }
            return;
        }

        // No cache hit. If the phone changed away from a verified number
        // OR an open OTP card now points at the wrong phone, reset.
        if (phoneChanged && (phoneVerified || otpCardOpen)) {
            setPhoneVerified(false);
            setAttestationToken('');
            setOtpCardOpen(false);
            setRequestId('');
            setInlineOtpDigits(['', '', '', '']);
            setInlineOtpError(undefined);
            setResendIn(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedIdentifier, watchedCountry]);

    // CR-2026-05-07a Phase 3E rev 5 — country-change side effects:
    //  - Reset phone-related state (different country = different format,
    //    so previous phone + OTP are invalid)
    //  - Auto-set the timezone preference based on the new country
    // Skips the very first render so initial defaults don't blow away
    // the user's typed-in state on mount.
    const COUNTRY_TZ: Partial<Record<SupportedCountryIso, string>> = {
        AE: 'Asia/Dubai',
        JO: 'Asia/Amman',
        SA: 'Asia/Riyadh',
        EG: 'Africa/Cairo',
        KW: 'Asia/Kuwait',
        BH: 'Asia/Bahrain',
        QA: 'Asia/Qatar',
        OM: 'Asia/Muscat',
        LB: 'Asia/Beirut',
        IQ: 'Asia/Baghdad',
        SD: 'Africa/Khartoum',
        PS: 'Asia/Hebron',
    };
    const isFirstCountryRenderRef = React.useRef(true);
    useEffect(() => {
        if (isFirstCountryRenderRef.current) {
            isFirstCountryRenderRef.current = false;
            return;
        }
        // Reset phone & verification state — user must re-enter & re-verify
        // because dial code + national format changed.
        setValue1('identifier', '', { shouldValidate: false });
        setNormalizedIdentifier('');
        setRequestId('');
        setAttestationToken('');
        setPhoneVerified(false);
        setOtpCardOpen(false);
        setInlineOtpDigits(['', '', '', '']);
        setInlineOtpError(undefined);
        setResendIn(0);
        // Auto-set timezone preference. Fall back to UAE if the new
        // country isn't in the explicit list (most signups will be UAE
        // or JO — launch markets — so the mapping above is exhaustive
        // for those). Other countries inherit a sensible default rather
        // than staying on whatever the previous country's tz was.
        const nextTz = COUNTRY_TZ[watchedCountry] ?? 'Asia/Dubai';
        setTzPref(nextTz);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedCountry]);

    // ── Step 2 form ─────────────────────────────────────────────────────────
    const {
        control: ctrl2,
        handleSubmit: submit2,
        reset: reset2,
        formState: { errors: err2 },
    } = useForm<SignupStep2Form>({
        resolver: zodResolver(signupStep2Schema) as any,
        defaultValues: { code: '' },
    });

    // ── Step 3 form (AB#1568, simplified in AB#1630) ────────────────────────
    //
    // AB#1630 — Step 3 used to also collect email here, leading to a
    // duplicate-email field bug (Step 1 optional + Step 3 required = same
    // user typing the same address twice). Email is now collected only at
    // Step 1, and the register call below reads `step1Values.email`.
    //
    // 2026-04-28 — RHF field names keep the `step3` prefix to avoid any
    // future cross-form collision with Step 1 controllers that also use
    // common names like `email`. Pure precaution.
    const {
        control: ctrl3,
        handleSubmit: submit3,
        watch: watch3,
        reset: reset3,
        setValue: setValue3,
        formState: { errors: err3 },
    } = useForm<SignupStep3Form>({
        resolver: zodResolver(signupStep3Schema) as any,
        defaultValues: { step3Password: '', step3ConfirmPassword: '' },
    });

    const watchedPassword = watch3('step3Password') || '';
    const passwordStrength = useMemo(
        () => getPasswordStrength(watchedPassword),
        [watchedPassword]
    );

    // ── Helpers ─────────────────────────────────────────────────────────────

    /** Map a Zod custom error message OR a backend error code to localized copy. */
    const mapIdentifierError = (message: string | undefined): string | undefined => {
        if (!message) return undefined;
        switch (message) {
            case 'phone_invalid':
                return t('signup.errors.phoneInvalid');
            default:
                return message;
        }
    };

    const mapCountryError = (message: string | undefined): string | undefined => {
        if (!message) return undefined;
        switch (message) {
            case 'country_not_supported':
                return t('signup.errors.countryNotSupported');
            default:
                return message;
        }
    };

    /** AB#1630 — Step 1 inline email errors. */
    const mapEmailErrorStep1 = (message: string | undefined): string | undefined => {
        if (!message) return undefined;
        switch (message) {
            case 'email_required':
                return t('signup.errors.emailRequired');
            case 'email_invalid':
            case 'Invalid email address':
                return t('signup.errors.emailInvalid');
            default:
                return message;
        }
    };

    /** Map a backend error envelope code to localized copy. */
    const mapBackendError = (code: string): string => {
        switch (code) {
            case 'rate_limited':
            case 'too_many_requests':
                return t('signup.errors.tooManyAttempts');
            case 'vendor_unavailable':
            case 'sms_provider_unavailable':
                return t('signup.errors.vendorUnavailable');
            case 'country_not_supported':
                return t('signup.errors.countryNotSupported');
            case 'identifier_already_registered':
            case 'phone_already_registered':
            case 'phone_in_use':
                return t('signup.errors.phoneAlreadyRegistered');
            case 'invalid_identifier':
            case 'invalid_phone':
            case 'invalid_phone_format':
                return t('signup.errors.phoneInvalid');
            default:
                // Surface the raw BE code in dev so we know what we're
                // missing in the map. Production strips it via NODE_ENV.
                // eslint-disable-next-line no-console
                console.warn('[signup] unmapped BE error code:', code);
                return code
                    ? `${t('signup.errors.sendFailed')} (${code})`
                    : t('signup.errors.sendFailed');
        }
    };

    // ── Handlers ────────────────────────────────────────────────────────────

    // CR-2026-05-07a Phase 3E rev 7 — Verify button (next to phone) is a
    // PHONE-ONLY action. It should NOT trip RHF's full Step 1 schema
    // (fullName / email / country) because the user might tap Verify
    // before filling those — name+email are needed for the final
    // /register call, not for the OTP request. Captcha is also strictly
    // an email-step concern.
    const onVerifyMobileClick = async () => {
        const wireIdentifier = normalizeE164(watchedIdentifier ?? '', watchedCountry);
        if (!wireIdentifier) {
            setFormError(t('signup.errors.phoneInvalid'));
            return;
        }
        // Phase 3E rev 9 — snapshot the current Step 1 values so
        // onStep3Submit (the final register call) has fullName + email
        // + country + phone available even though the merged flow no
        // longer goes through submit1(onStep1Submit). The snapshot
        // gets refreshed at register-time too in case the user types
        // their name after clicking Verify.
        const snapshotStep1 = (): SignupStep1Form => ({
            country: watchedCountry as SupportedCountryIso,
            identifier: watchedIdentifier ?? '',
            fullName: (watch1('fullName') ?? '').toString(),
            email: emailStepValue,
        });
        // Skip the BE call entirely if we already verified this phone
        // earlier in the same session (covers AB#1667 — country round-trip
        // restoring a phone that was previously verified).
        const cached = verifiedPhonesRef.current.get(wireIdentifier);
        if (cached) {
            setNormalizedIdentifier(wireIdentifier);
            setAttestationToken(cached);
            setPhoneVerified(true);
            setOtpCardOpen(false);
            setStep1Values(snapshotStep1());
            setFormError(undefined);
            return;
        }
        setFormError(undefined);
        setLoading(true);
        try {
            const res = await apiService.otpRequest({
                identifier: wireIdentifier,
                channel: 'sms',
                purpose: 'signup',
            });
            setNormalizedIdentifier(wireIdentifier);
            setStep1Values(snapshotStep1());
            setRequestId(res.requestId);
            setResendIn(res.resendIn);
            setInlineOtpDigits(['', '', '', '']);
            setInlineOtpError(undefined);
            setOtpCardOpen(true);
            setTimeout(() => inlineOtpRefs.current[0]?.focus(), 50);
        } catch (err: any) {
            const errBag = err?.response?.data?.errors ?? err?.response?.data?.Errors ?? {};
            const code: string = errBag.code?.[0] ?? '';
            if (!code && !err?.response) {
                setFormError(t('signup.errors.networkError'));
            } else {
                setFormError(mapBackendError(code));
            }
        } finally {
            setLoading(false);
        }
    };

    // The legacy onStep1Submit path remains for backwards compatibility
    // (routes from the legacy 3-step flow / the form's submit handler).
    // The merged identifier step now uses onVerifyMobileClick instead.
    const onStep1Submit = async (values: SignupStep1Form) => {
        setFormError(undefined);

        const wireIdentifier = normalizeE164(values.identifier, values.country);
        if (!wireIdentifier) {
            setFormError(t('signup.errors.phoneInvalid'));
            return;
        }

        setLoading(true);
        try {
            const res = await apiService.otpRequest({
                identifier: wireIdentifier,
                channel: 'sms',
                purpose: 'signup',
            });
            setStep1Values(values);
            setNormalizedIdentifier(wireIdentifier);
            setRequestId(res.requestId);
            setResendIn(res.resendIn);
            setInlineOtpDigits(['', '', '', '']);
            setInlineOtpError(undefined);
            setOtpCardOpen(true);
            // Focus the first digit box once it mounts
            setTimeout(() => inlineOtpRefs.current[0]?.focus(), 50);
        } catch (err: any) {
            // BE returns the error bag as lowercase `errors` per ApiResult
            // contract; some legacy paths may serialize it PascalCase. Read
            // both so a casing change on either side never silently masks
            // the code.
            const errBag = err?.response?.data?.errors ?? err?.response?.data?.Errors ?? {};
            const code: string = errBag.code?.[0] ?? '';
            if (!code && !err?.response) {
                setFormError(t('signup.errors.networkError'));
            } else {
                setFormError(mapBackendError(code));
            }
        } finally {
            setLoading(false);
        }
    };

    // Verify the 4-digit OTP. Auto-fired when all 4 digits filled.
    // Phase 3E rev 9 — guard relaxed: only `requestId` is required. The
    // legacy `step1Values` snapshot was only ever set by the old
    // onStep1Submit submit-form path, which the merged identifier step
    // bypasses. step1Values is not used in the verify call itself
    // (verify takes requestId + code + identifier); it's used later in
    // onStep3Submit which now reads from watch1 directly.
    const onInlineOtpVerify = async (digits?: string[]) => {
        const codeArr = digits ?? inlineOtpDigits;
        const code = codeArr.join('');
        if (!requestId) return;
        if (!/^\d{4,6}$/.test(code)) {
            setInlineOtpError(t('signup.otp.invalidCode'));
            return;
        }
        setInlineOtpError(undefined);
        setLoading(true);
        try {
            const res = await apiService.otpVerify({
                requestId,
                code,
                identifier: normalizedIdentifier,
            });
            if (res.tokenType === 'bearer' && res.accessToken && res.refreshToken) {
                setTokens(res.accessToken, res.refreshToken);
                navigate('/onboarding');
                return;
            }
            if (res.tokenType === 'signup_scoped' && res.accessToken) {
                setAttestationToken(res.accessToken);
                setPhoneVerified(true);
                setOtpCardOpen(false);
                // AB#1667 — cache attestation against the E.164 phone so we
                // can skip re-verify if the user country-toggles back to it.
                if (normalizedIdentifier) {
                    verifiedPhonesRef.current.set(normalizedIdentifier, res.accessToken);
                }
                return;
            }
            setInlineOtpError(t('signup.errors.network'));
        } catch (err: any) {
            const errBag2 = err?.response?.data?.errors ?? err?.response?.data?.Errors ?? {};
            const code2: string = errBag2.code?.[0] ?? '';
            if (code2 === 'invalid_code') {
                setInlineOtpError(t('signup.errors.otpInvalid'));
            } else if (code2 === 'otp_expired') {
                setInlineOtpError(t('signup.errors.otpExpired'));
            } else if (code2 === 'too_many_attempts') {
                setInlineOtpError(t('signup.errors.tooManyAttempts'));
            } else {
                setInlineOtpError(t('signup.errors.network'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Per-box change handler with auto-focus to next box + auto-verify on
    // the 4th digit. Backspace on an empty box moves focus to the previous.
    //
    // Phase 3E rev 8 — explicitly fold Eastern Arabic-Indic numerals
    // (U+0660 ٠–٩ U+0669) and Persian numerals (U+06F0 ۰–۹ U+06F9) to
    // Western 0-9 before the regex strip. Some Windows / Arabic-locale
    // input methods produce these characters when the user hits the
    // numpad; without the fold, value.replace(/\D/g, '') would discard
    // them as non-digits.
    const FOLD_DIGITS_RE = /[٠-٩۰-۹]/g;
    const foldEasternDigits = (s: string): string =>
        s.replace(FOLD_DIGITS_RE, (ch) => {
            const code = ch.charCodeAt(0);
            if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
            if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0);
            return ch;
        });

    const onOtpDigitChange = (index: number, value: string) => {
        const folded = foldEasternDigits(value);
        const digit = folded.replace(/\D/g, '').slice(-1); // last digit typed
        const next = [...inlineOtpDigits];
        next[index] = digit;
        setInlineOtpDigits(next);
        setInlineOtpError(undefined);
        if (digit && index < 3) {
            inlineOtpRefs.current[index + 1]?.focus();
        }
        if (digit && index === 3 && next.every((d) => d.length === 1)) {
            onInlineOtpVerify(next);
        }
    };

    const onOtpDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !inlineOtpDigits[index] && index > 0) {
            inlineOtpRefs.current[index - 1]?.focus();
        }
    };

    const onInlineOtpResend = async () => {
        if (!normalizedIdentifier || resendIn > 0) return;
        setInlineOtpError(undefined);
        setLoading(true);
        try {
            const res = await apiService.otpRequest({
                identifier: normalizedIdentifier,
                channel: 'sms',
                purpose: 'signup',
            });
            setRequestId(res.requestId);
            setResendIn(res.resendIn);
            setInlineOtpDigits(['', '', '', '']);
            setTimeout(() => inlineOtpRefs.current[0]?.focus(), 50);
        } catch {
            setInlineOtpError(t('signup.errors.sendFailed'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Step 2 (OTP verify) — AB#1568 redesign.
     *
     * On success, the BE returns `tokenType: 'signup_scoped'` (lowercase
     * snake_case — see `OtpVerifyV2Response.cs`) plus a narrow-scope
     * accessToken. We DO NOT call `setTokens` here — that token can't pass
     * `/onboarding`'s `RequireRole(SalonOwner)` gate. Instead we hold the
     * token in memory and advance to Step 3, where it rides as the
     * Authorization header on `/v1/auth/register` so the BE can mark the
     * new credential phone-verified.
     *
     * Defensive: if the BE ever returns a `bearer` token here (e.g. login
     * path on an already-existing user), short-circuit straight to home.
     */
    const onStep2Submit = async (values: SignupStep2Form) => {
        if (!step1Values) return;
        setFormError(undefined);
        setLoading(true);
        try {
            const res = await apiService.otpVerify({
                requestId,
                code: values.code,
                identifier: normalizedIdentifier,
            });

            if (res.tokenType === 'bearer' && res.accessToken && res.refreshToken) {
                // Defensive — full session means the user already exists.
                // Treat as a successful login and skip the rest of signup.
                setTokens(res.accessToken, res.refreshToken);
                navigate('/onboarding');
                return;
            }

            if (res.tokenType === 'signup_scoped' && res.accessToken) {
                setAttestationToken(res.accessToken);
                // AB#1630 — Step 3 no longer asks for email; just clear
                // the password fields for a clean Set Password screen.
                reset3({ step3Password: '', step3ConfirmPassword: '' });
                // CR-2026-05-06 — history push so browser-back walks
                // OTP → Step 1 instead of exiting the route.
                window.history.pushState(
                    { signupStep: 'password' },
                    '',
                    window.location.pathname,
                );
                setStep('password');
                return;
            }

            // Unexpected shape — surface a generic error.
            setFormError(t('signup.errors.network'));
        } catch (err: any) {
            // BE returns the error bag as lowercase `errors` per ApiResult
            // contract; some legacy paths may serialize it PascalCase. Read
            // both so a casing change on either side never silently masks
            // the code.
            const errBag = err?.response?.data?.errors ?? err?.response?.data?.Errors ?? {};
            const code: string = errBag.code?.[0] ?? '';
            if (code === 'identifier_already_registered') {
                setFormError(t('signup.errors.alreadyRegistered'));
                showToast(t('signup.errors.alreadyRegistered'), 'info');
                toggleForm('login');
            } else if (code === 'invalid_code') {
                setFormError(t('signup.errors.otpInvalid'));
            } else if (code === 'otp_expired') {
                setFormError(t('signup.errors.otpExpired'));
            } else if (code === 'too_many_attempts') {
                setFormError(t('signup.errors.tooManyAttempts'));
            } else {
                setFormError(t('signup.errors.network'));
            }
        } finally {
            setLoading(false);
        }
    };

    /**
     * Step 3 (password + register + auto-login) — AB#1568.
     *
     * - POST /v1/auth/register with the signup_scoped Bearer in the
     *   Authorization header. BE validates the token, binds it to the
     *   phone, and marks the new AuthCredential as IsVerified=true /
     *   PhoneVerified=true (skipping the email-link gate).
     * - POST /v1/auth/login with email+password to mint the full Bearer.
     * - setTokens, navigate /onboarding.
     *
     * Failure modes:
     * - 401 from register → signup_scoped expired or rejected → reset to
     *   Step 1 with a clear toast.
     * - 4xx with Email already registered → suggest signing in.
     * - 200 from register but login throws → toast + bounce to login form
     *   with email pre-filled.
     */
    const onStep3Submit = async (values: SignupStep3Form) => {
        // Phase 3E rev 9 — refresh step1Values from currently-watched
        // form fields so a fullName typed after Verify-click is included.
        // Falls back to the snapshot taken at Verify-time if the watch
        // returns empty (defensive — this path shouldn't happen).
        const liveFullName = (watch1('fullName') ?? '').toString().trim();
        const liveCountry = watch1('country') as SupportedCountryIso;
        const liveIdentifier = (watch1('identifier') ?? '').toString();
        const effectiveStep1: SignupStep1Form = {
            country: liveCountry || (step1Values?.country as SupportedCountryIso),
            identifier: liveIdentifier || (step1Values?.identifier ?? ''),
            fullName: liveFullName || (step1Values?.fullName ?? '').trim(),
            email: emailStepValue || (step1Values?.email ?? ''),
        };
        const step1Email = (effectiveStep1.email ?? '').trim();

        // Phase 3E rev 9 — manual validation of the Step-1 fields the
        // merged flow no longer runs through the RHF schema. The Step 1
        // schema's full superRefine doesn't fire here because Create
        // account submits the Step 3 form, not Step 1.
        if (effectiveStep1.fullName.length < 2) {
            setFormError(t('signup.errors.fullNameTooShort'));
            return;
        }
        if (!attestationToken) {
            setFormError(t('signup.errors.verificationExpired'));
            handleChangeIdentifier();
            return;
        }
        if (step1Email.length === 0) {
            setFormError(t('signup.errors.emailRequired'));
            return;
        }
        setFormError(undefined);
        setLoading(true);
        try {
            // AB#1568 — split E.164 properly via libphonenumber-js. The
            // earlier regex `replace(/^\+\d+/, '')` was greedy and ate the
            // entire phone, leaving `phoneNumber=""` and the full E.164 in
            // `countryCode`, which the BE rejected with 500. Helper uses
            // libphonenumber's `countryCallingCode` + `nationalNumber`.
            const split = splitE164ForBackend(normalizedIdentifier);
            if (!split) {
                setFormError(t('signup.errors.phoneInvalid'));
                return;
            }
            await apiService.register(
                {
                    email: step1Email,
                    password: values.step3Password,
                    fullName: effectiveStep1.fullName.trim(),
                    phoneNumber: split.phoneNumber,
                    countryCode: split.countryCode,
                    termsAccepted: true,
                    userType: ProfileType.SalonOwner,
                },
                attestationToken
            );

            // Auto-login: the BE marked the credential IsVerified=true so
            // the legacy /v1/auth/login gate (`if (!IsVerified) reject`)
            // passes. apiService.login internally calls setTokens.
            try {
                await apiService.login({
                    identifier: step1Email,
                    password: values.step3Password,
                    userType: ProfileType.SalonOwner,
                });
                // AB#1612 Phase 1A.2 — fresh-register users always have
                // EmailVerified=false (per RegisterCommandHandler comment in
                // BE). Set the soft-gate flag so the global banner shows on
                // /onboarding and beyond, prompting Resend verification email.
                setEmailVerificationRequired(true);
                setPendingVerificationEmail(step1Email);
                navigate('/onboarding');
            } catch {
                // Edge case: register succeeded but login race / rate-limit.
                // Push the user to the login form with email pre-filled so
                // they don't have to re-type. Account exists.
                showToast(t('signup.errors.autoLoginFailed'), 'info');
                toggleForm('login');
            }
        } catch (err: any) {
            const status = err?.response?.status;
            const errorsBag = err?.response?.data?.Errors ?? err?.response?.data?.errors ?? {};
            const emailErrors: string[] = errorsBag.Email ?? errorsBag.email ?? [];
            const phoneErrors: string[] = errorsBag.PhoneNumber ?? errorsBag.phoneNumber ?? [];

            if (status === 401 || status === 403) {
                // Attestation rejected (expired / mismatch / replay). Force
                // a fresh OTP — there's no path forward without one.
                setFormError(t('signup.errors.verificationExpired'));
                showToast(t('signup.errors.verificationExpired'), 'error');
                handleChangeIdentifier();
            } else if (
                emailErrors.some((m) => /already/i.test(m)) ||
                phoneErrors.some((m) => /already/i.test(m))
            ) {
                // AB#1630 AC-4 — silent failure: surface the error and
                // bounce back to Step 1 so the user can edit the email
                // (and re-OTP). Do NOT auto-redirect to the login form —
                // they may simply have mistyped one character.
                // Phase 3E rev 16 — distinguish phone-already vs email-already
                // so the user is told which identifier collided. Previously
                // we always showed the email message even when BE flagged
                // the phone (response `errors.PhoneNumber: [...]`).
                const phoneTaken = phoneErrors.some((m) => /already/i.test(m));
                const emailTaken = emailErrors.some((m) => /already/i.test(m));
                const messageKey = phoneTaken && !emailTaken
                    ? 'signup.errors.phoneAlreadyRegistered'
                    : 'signup.errors.emailAlreadyRegistered';
                setFormError(t(messageKey));
                showToast(t(messageKey), 'info');
                handleChangeIdentifier();
            } else if (!err?.response) {
                setFormError(t('signup.errors.networkError'));
            } else {
                setFormError(t('signup.errors.registerFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = useCallback(async () => {
        if (!normalizedIdentifier || resendIn > 0) return;
        setFormError(undefined);
        setLoading(true);
        try {
            const res = await apiService.otpRequest({
                identifier: normalizedIdentifier,
                channel: 'sms',
                purpose: 'signup',
            });
            setRequestId(res.requestId);
            setResendIn(res.resendIn);
            reset2({ code: '' });
        } catch {
            setFormError(t('signup.errors.sendFailed'));
        } finally {
            setLoading(false);
        }
    }, [normalizedIdentifier, resendIn, reset2, t]);

    const handleChangeIdentifier = () => {
        setStep('identifier');
        setFormError(undefined);
        // Note: Step 1 form values (incl. email) are intentionally
        // preserved so the user can tweak ONE field (e.g. fix a
        // typo in email per AB#1630 AC-4) without retyping everything.
        // The "already registered" / "verification expired" surfacing is
        // handled via showToast at the call site, not formError.
        reset2({ code: '' });
        // CR-2026-05-07a Phase 3E rev 14 — DO NOT clear the password here.
        // In the legacy 3-step flow this used to live on a hidden Step 3
        // and was harmless cleanup; in the merged identifier step the
        // password input is always visible, so resetting it on a register
        // error makes the user retype everything they just typed. The
        // password is also harmless to keep across an attestation refresh
        // (it's never sent to the BE without a fresh attestation token,
        // and Step 3 schema validation runs on every Create account click).
        // Only the attestation/verified state needs clearing — the user
        // re-clicks Verify to get a fresh OTP, then re-clicks Create
        // account; their password is still in the field.
        setAttestationToken('');
        setPhoneVerified(false);
        setOtpCardOpen(false);
        setRequestId('');
        setInlineOtpDigits(['', '', '', '']);
        setInlineOtpError(undefined);
        setResendIn(0);
    };

    // ── Guard: feature flag ─────────────────────────────────────────────────
    // Redirect to login when Auth v2 is disabled — no "coming soon" dead-end.
    if (!isAuthV2Enabled()) {
        return <Navigate to="/form/login" replace />;
    }

    // ── Step 0 — Email entry + SSO providers (Phase 3) ─────────────────────
    // Fresha-style first-step screen. Email collected here is carried into
    // the rest of the signup via emailStepValue and forwarded to the BE
    // register call. Phone-primary identity model is unchanged (AB#1576) —
    // email is a secondary contact attribute, not a login identifier.
    //
    // SSO buttons (Facebook / Google / Apple) are placeholders today:
    // OAuth wiring + AAD B2C IDP integration is a separate BE epic. Click
    // surfaces a "Coming soon" toast so users know the surface exists.
    if (step === 'email') {
        const onEmailContinue = (e: React.FormEvent) => {
            e.preventDefault();
            const trimmed = emailStepValue.trim();
            if (trimmed.length === 0) {
                setFormError(t('signup.email.errors.required'));
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                setFormError(t('signup.email.errors.invalid'));
                return;
            }
            setFormError(undefined);

            // CR-2026-05-07a Phase 3E rev 12 — pre-auth email-verification
            // call REMOVED. The BE endpoint POST /v2/auth/request-email-
            // -verification is [Authorize]-protected by design (per
            // wiki AB1612 Phase 1B - Email Verification Commands) — it's
            // a POST-AUTH gate for already-signed-in owners, not a
            // pre-register email confirmation. The previous fire-and-
            // forget call from this handler always 401'd silently, AND
            // the "Verification email sent" toast lied to the user about
            // an email that was never sent. Both removed here.
            //
            // The proper "anonymous email check" pattern (always-200
            // enumeration-protected /v1/auth/identify) is scoped under
            // AB#1665 (Identity-first auth, Sprint 5+). We still fire
            // the captcha here because the form has it; the token is
            // captured but no longer forwarded anywhere — kept for the
            // moment so removing the widget doesn't break layout in this
            // hotfix. Captcha cleanup is a follow-up.
            (async () => {
                try {
                    await recaptchaRef.current?.executeAsync();
                    recaptchaRef.current?.reset();
                } catch {
                    /* captcha load/timeout — harmless, pre-auth flow */
                }
            })();

            // Push history so browser-back from identifier step returns
            // here (the email step) instead of exiting /form/signup.
            window.history.pushState(
                { signupStep: 'identifier' },
                '',
                window.location.pathname,
            );
            setStep('identifier');
        };

        const onSsoClick = (provider: string) => {
            showToast(t('signup.email.ssoComingSoon', { provider }), 'info');
        };

        return (
            <form
                className={styles.signupForm}
                onSubmit={onEmailContinue}
                aria-label={t('signup.email.title')}
                noValidate
                autoComplete="off"
            >
                <div>
                    <p className={styles.signupText}>{t('signup.step1TitleFresha')}</p>
                    <p className={styles.signupSubtext}>{t('signup.step1SubtitleFresha')}</p>
                </div>

                {/* CR-2026-05-07a Phase 3 — Sign-in link moved up here from
                    the footer per founder feedback. Existing users land on
                    /form/signup by mistake often (marketing CTAs, browser
                    autofill, partner referral links) and burying the
                    sign-in link under reCAPTCHA / footer made it invisible.
                    Prominent location matches Stripe / Auth0 / Clerk
                    convention. */}
                <div className={styles.signInLinkTop}>
                    <span>{t('signup.alreadyHaveAccount')}</span>{' '}
                    <button
                        type="button"
                        onClick={() => toggleForm('login')}
                        className={styles.linkBtnTop}
                        disabled={loading}
                    >
                        {t('signup.signIn')}
                    </button>
                </div>

                {formError && (
                    <div className={styles.formError} role="alert" aria-live="polite">
                        <span aria-hidden="true">⚠</span> {formError}
                    </div>
                )}

                <div className={styles.signupFields}>
                    <InputField
                        label={t('signup.emailLabel')}
                        value={emailStepValue}
                        onChange={(v) => setEmailStepValue(v)}
                        placeholder={t('signup.emailPlaceholder')}
                        type="email"
                        name="email"
                        autoComplete="off"
                        disabled={loading}
                    />
                </div>

                {/* CR-2026-05-07a Phase 3 — INVISIBLE reCAPTCHA. No widget
                    rendered; ref.executeAsync() called from the submit
                    handler. Badge is hidden via CSS (.grecaptcha-badge);
                    Google's branding requirement is satisfied by the
                    "protected by reCAPTCHA" disclaimer in the footer. */}
                <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    size="invisible"
                    theme="dark"
                    badge="bottomright"
                    hl={locale}
                />

                <div className={styles.buttonWrpr}>
                    <Button
                        type="submit"
                        label={t('signup.email.continue')}
                        variant="primary"
                    />
                </div>

                <div className={styles.ssoDivider}>
                    <span>{t('signup.email.or')}</span>
                </div>

                <div className={styles.ssoStack}>
                    <button
                        type="button"
                        className={styles.ssoBtn}
                        onClick={() => onSsoClick('Facebook')}
                        disabled={loading}
                    >
                        <svg className={styles.ssoIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3l-.5 3H13v6.95c5.05-.5 9-4.76 9-9.95z" fill="#1877F2"/>
                        </svg>
                        <span>{t('signup.email.continueFacebook')}</span>
                    </button>
                    <button
                        type="button"
                        className={styles.ssoBtn}
                        onClick={() => onSsoClick('Google')}
                        disabled={loading}
                    >
                        <svg className={styles.ssoIcon} viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#4285F4" d="M23.06 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h6.21c-.27 1.44-1.08 2.66-2.3 3.48v2.89h3.72c2.18-2 3.43-4.95 3.43-8.38z"/>
                            <path fill="#34A853" d="M12 24c3.11 0 5.71-1.03 7.61-2.79l-3.72-2.89c-1.03.69-2.35 1.1-3.89 1.1-2.99 0-5.53-2.02-6.43-4.74H1.74v2.98C3.63 21.43 7.51 24 12 24z"/>
                            <path fill="#FBBC05" d="M5.57 14.68A7.21 7.21 0 0 1 5.18 12c0-.93.16-1.83.39-2.68V6.34H1.74A12.01 12.01 0 0 0 0 12c0 1.94.46 3.77 1.74 5.66l3.83-2.98z"/>
                            <path fill="#EA4335" d="M12 4.75c1.69 0 3.2.58 4.4 1.72l3.3-3.3C17.7 1.18 15.1 0 12 0 7.51 0 3.63 2.57 1.74 6.34l3.83 2.98C6.47 6.77 9.01 4.75 12 4.75z"/>
                        </svg>
                        <span>{t('signup.email.continueGoogle')}</span>
                    </button>
                    <button
                        type="button"
                        className={styles.ssoBtn}
                        onClick={() => onSsoClick('Apple')}
                        disabled={loading}
                    >
                        <svg className={styles.ssoIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M16.36 1c.06.97-.32 1.92-.96 2.6-.7.74-1.83 1.33-2.94 1.24-.07-.93.4-1.9.99-2.5C14.18 1.55 15.34.97 16.36 1zm3.5 17.05c-.51 1.18-.76 1.71-1.42 2.75-.92 1.45-2.21 3.25-3.81 3.27-1.43.01-1.79-.93-3.72-.92-1.93.01-2.33.94-3.76.92-1.6-.02-2.83-1.65-3.74-3.1C2 17.31.98 13.27 2.78 10.55c1.27-1.93 3.27-3.06 5.16-3.06 1.92 0 3.13 1.06 4.72 1.06 1.54 0 2.48-1.06 4.7-1.06 1.68 0 3.46.92 4.74 2.5-4.16 2.28-3.49 8.22-2.24 7.06z"/>
                        </svg>
                        <span>{t('signup.email.continueApple')}</span>
                    </button>
                </div>

                <hr className={styles.signupDivider} aria-hidden="true" />

                <div className={styles.appPitchRow}>
                    <p className={styles.appPitchTitle}>{t('signup.customerCta.title')}</p>
                    <p className={styles.appPitchSub}>{t('signup.customerCta.sub')}</p>
                    <div className={styles.appBadgeRow}>
                        <a className={styles.appBadge} href={t('signup.customerCta.appStoreUrl')} target="_blank" rel="noopener noreferrer" aria-label={t('signup.customerCta.appStoreAria')}>
                            <svg className={styles.appBadgeIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M16.36 1c.06.97-.32 1.92-.96 2.6-.7.74-1.83 1.33-2.94 1.24-.07-.93.4-1.9.99-2.5C14.18 1.55 15.34.97 16.36 1zm3.5 17.05c-.51 1.18-.76 1.71-1.42 2.75-.92 1.45-2.21 3.25-3.81 3.27-1.43.01-1.79-.93-3.72-.92-1.93.01-2.33.94-3.76.92-1.6-.02-2.83-1.65-3.74-3.1C2 17.31.98 13.27 2.78 10.55c1.27-1.93 3.27-3.06 5.16-3.06 1.92 0 3.13 1.06 4.72 1.06 1.54 0 2.48-1.06 4.7-1.06 1.68 0 3.46.92 4.74 2.5-4.16 2.28-3.49 8.22-2.24 7.06z"/>
                            </svg>
                            <span className={styles.appBadgeStack}>
                                <span className={styles.appBadgeSmall}>{t('signup.customerCta.appStorePre')}</span>
                                <span className={styles.appBadgeBig}>{t('signup.customerCta.appStore')}</span>
                            </span>
                        </a>
                        <a className={styles.appBadge} href={t('signup.customerCta.playStoreUrl')} target="_blank" rel="noopener noreferrer" aria-label={t('signup.customerCta.playStoreAria')}>
                            <svg className={styles.appBadgeIcon} viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#34A853" d="M3 20.5V3.5c0-.6.3-1.1.8-1.4l10 8.4-10 10z"/>
                                <path fill="#FBBC04" d="M16.8 12.4l-3.4-2.9 2-1.7L18.5 9c.6.4.6 1.3 0 1.7l-1.7 1.7z"/>
                                <path fill="#EA4335" d="M3.8 2.1L13.4 10l2-1.7L4.5 2.1c-.2-.1-.5-.1-.7 0z"/>
                                <path fill="#4285F4" d="M3.8 21.9l9.6-7.9-2-1.7-7.6 7.6c-.3.4-.3.9 0 1.3v.7z"/>
                            </svg>
                            <span className={styles.appBadgeStack}>
                                <span className={styles.appBadgeSmall}>{t('signup.customerCta.playStorePre')}</span>
                                <span className={styles.appBadgeBig}>{t('signup.customerCta.playStore')}</span>
                            </span>
                        </a>
                    </div>
                </div>

                {/* Phase 3 — passive 'By clicking Send code, you agree to...'
                    disclosure is intentionally NOT shown on the email step
                    (founder spec). Button here is 'Continue', not 'Send code',
                    and the user hasn't yet committed to a registration action.
                    The Devanza terms acceptance happens at Step 2 (Send code)
                    where the OTP is dispatched. The reCAPTCHA disclaimer stays
                    since it's a Google branding requirement when the badge is
                    hidden. */}
                <p className={styles.recaptchaDisclaimer} role="note">
                    {t('signup.recaptchaDisclaimer.prefix')}{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className={styles.linkBtn}>
                        {t('signup.recaptchaDisclaimer.privacy')}
                    </a>
                    {' '}{t('signup.recaptchaDisclaimer.connector')}{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className={styles.linkBtn}>
                        {t('signup.recaptchaDisclaimer.terms')}
                    </a>
                    {t('signup.recaptchaDisclaimer.suffix')}
                </p>

                {/* Sign-in link relocated to top of the form (above the
                    email field) per founder feedback — see comment there. */}
            </form>
        );
    }

    // ── Error mappers shared by the merged identifier step + the legacy
    // password-step path. Declared before the early return so the merged
    // identifier JSX can reference them without hitting TDZ. ──────────────
    const mapPasswordError = (message: string | undefined): string | undefined => {
        if (!message) return undefined;
        // CR-2026-05-07a Phase 3E rev 4 — typed error codes from the
        // hardened complexity schema. Map each to localized guidance.
        if (message === 'password_too_short') return t('signup.errors.passwordTooShort');
        if (message === 'password_missing_lowercase') return t('signup.errors.passwordMissingLowercase');
        if (message === 'password_missing_uppercase') return t('signup.errors.passwordMissingUppercase');
        if (message === 'password_missing_digit') return t('signup.errors.passwordMissingDigit');
        if (message === 'password_missing_special') return t('signup.errors.passwordMissingSpecial');
        if (/at least 8/i.test(message)) return t('signup.errors.passwordTooShort');
        return message;
    };
    const mapConfirmPasswordError = (message: string | undefined): string | undefined => {
        if (!message) return undefined;
        if (message === 'password_mismatch') return t('signup.errors.passwordMismatch');
        return message;
    };

    // ── Step 1 — Identifier (country/phone/name) ───────────────────────────
    if (step === 'identifier') {
        // Phase 3E rev 11 — back link to Step 1 (email entry). Founder
        // spec: discoverable affordance to edit email after advancing.
        // Browser-back already works (we pushState on the email→identifier
        // transition) but isn't discoverable; an in-form button is.
        const onBackToEmail = () => {
            window.history.back();
        };

        return (
            <form
                className={styles.signupForm}
                onSubmit={submit1(onStep1Submit)}
                aria-label={t('signup.step1Title')}
                noValidate
                autoComplete="off"
            >
                {/* Phase 3E rev 11 — back link sits at the top of the form
                    above the title (Booksy / Stripe / Fresha pattern). */}
                {/* Phase 3E rev 15 — icon-only back, anchored to the
                    logo row via absolute positioning so it stops eating
                    a full vertical row of its own. Positioning rules
                    live in layout.module.css under .frescaTheme. */}
                <button
                    type="button"
                    className={styles.backLink}
                    onClick={onBackToEmail}
                    aria-label={t('signup.back')}
                    title={t('signup.back')}
                >
                    <svg className={styles.backLinkIcon} viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            d="M15 18l-6-6 6-6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                <div>
                    {/* CR-2026-05-07a — Fresha-style title pattern */}
                    <p className={styles.signupText}>{t('signup.step1TitleFresha')}</p>
                    <p className={styles.signupSubtext}>{t('signup.step1SubtitleFresha')}</p>
                </div>

                {formError && (
                    <div className={styles.formError} role="alert" aria-live="polite">
                        <span aria-hidden="true">⚠</span> {formError}
                    </div>
                )}

                {/* CR-2026-05-05a — waitlist banner removed. All non-banned
                    countries are active for signup; banlist filtering happens
                    at the seed level so users only ever pick valid countries. */}

                {/* CR-2026-05-05b — field order: Name → Email → Country → Phone.
                    Country still sits ABOVE phone so the dial-code segment in
                    PhoneSegmented reflects the country selection before the
                    user types digits. */}
                <div className={styles.signupFields}>
                    <Controller
                        name="fullName"
                        control={ctrl1}
                        render={({ field }) => (
                            <InputField
                                label={t('signup.fullNameLabel')}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={t('signup.fullNamePlaceholder')}
                                type="text"
                                name="fullName"
                                autoComplete="name"
                                disabled={loading}
                                feedback={err1.fullName ? 'error' : undefined}
                                feedbackMessage={err1.fullName?.message}
                            />
                        )}
                    />
                    {/* Phase 3 — Email is now captured at the new email-entry
                        step (Step 0). The Controller stays mounted so the RHF
                        schema still validates email before /v1/auth/register;
                        the visible InputField is suppressed only when
                        emailStepValue has already been provided. If the user
                        navigates back, they edit email there, not here. */}
                    {!emailStepValue && (
                        <Controller
                            name="email"
                            control={ctrl1}
                            render={({ field }) => (
                                <InputField
                                    label={t('signup.emailLabel')}
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    placeholder="name@example.com"
                                    type="email"
                                    name="email"
                                    autoComplete="off"
                                    disabled={loading}
                                    feedback={err1.email ? 'error' : undefined}
                                    feedbackMessage={mapEmailErrorStep1(err1.email?.message)}
                                />
                            )}
                        />
                    )}
                    <Controller
                        name="country"
                        control={ctrl1}
                        render={({ field }) => (
                            <CountrySelect
                                label={t('signup.countryLabel')}
                                value={field.value}
                                onChange={field.onChange}
                                disabled={loading}
                                locale={locale}
                                feedback={err1.country ? 'error' : undefined}
                                feedbackMessage={mapCountryError(err1.country?.message as string | undefined)}
                                searchPlaceholder={t('signup.country.searchPlaceholder')}
                                noResultsLabel={t('signup.country.noResults')}
                            />
                        )}
                    />
                    {/* Phase 3E rev 4 — phone field row with INLINE Verify
                        button on the right (Booksy/Stripe pattern) and the
                        green check moved INTO the phone segmented control
                        (right-anchored, vertically centered) once verified. */}
                    <div className={styles.phoneRow}>
                        <div className={styles.phoneFieldGrow}>
                            <Controller
                                name="identifier"
                                control={ctrl1}
                                render={({ field }) => (
                                    <PhoneSegmented
                                        label={t('signup.phoneLabel')}
                                        country={selectedCountry}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={t('signup.phoneNationalPlaceholder')}
                                        name="identifier"
                                        autoComplete="off"
                                        disabled={loading || phoneVerified}
                                        locale={locale}
                                        feedback={
                                            phoneVerified
                                                ? 'success'
                                                : err1.identifier
                                                    ? 'error'
                                                    : undefined
                                        }
                                        feedbackMessage={
                                            phoneVerified
                                                ? undefined
                                                : mapIdentifierError(err1.identifier?.message)
                                        }
                                    />
                                )}
                            />
                            {/* Phase 3E rev 4 — silent green check, anchored
                                INSIDE the phone segmented control on the
                                right edge, vertically centered. */}
                            {phoneVerified && (
                                <span className={styles.phoneInlineCheck} aria-label={t('signup.otp.verifiedBadge')}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </span>
                            )}
                        </div>
                        {/* Phase 3E rev 4 — inline Verify button on the same
                            row as the phone field. Disappears once verified. */}
                        {!phoneVerified && !otpCardOpen && (
                            <button
                                type="button"
                                className={styles.verifyMobileBtn}
                                /* Phase 3E rev 7 — phone-only handler.
                                   Bypasses the full Step 1 RHF schema so
                                   tapping Verify doesn't ask for name /
                                   email / captcha that the OTP request
                                   doesn't need. */
                                onClick={onVerifyMobileClick}
                                disabled={loading}
                            >
                                {loading ? t('signup.sendingCode') : t('signup.otp.verify')}
                            </button>
                        )}
                    </div>

                    {/* OTP digit boxes — appear immediately below the mobile
                        field once Send code is clicked, hide after success
                        (CR-2026-05-07a Phase 3E rev 2). 4 boxes per smoke
                        OTP, schema accepts 4-6 digits. Auto-focus + auto-
                        verify on the 4th digit. */}
                    {otpCardOpen && !phoneVerified && (
                        <div className={styles.otp4Card}>
                            <div className={styles.otp4Header}>
                                <span>{t('signup.otp.codeSentTo', { identifier: normalizedIdentifier })}</span>
                                <button
                                    type="button"
                                    className={styles.linkBtn}
                                    onClick={onInlineOtpResend}
                                    disabled={loading || resendIn > 0}
                                >
                                    {resendIn > 0
                                        ? t('signup.otp.resendIn', { seconds: resendIn })
                                        : t('signup.otp.resend')}
                                </button>
                            </div>
                            <div className={styles.otp4Boxes}>
                                {[0, 1, 2, 3].map((idx) => (
                                    <input
                                        key={idx}
                                        ref={(el) => { inlineOtpRefs.current[idx] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="one-time-code"
                                        maxLength={1}
                                        className={styles.otp4Box}
                                        /* Phase 3E rev 8 — force LTR + lang=en
                                           on each digit box. Without these,
                                           some Windows / Arabic-locale system
                                           fonts auto-substitute Western 0-9
                                           with Eastern Arabic numerals
                                           (٠-٩), which the BE OTP-verify
                                           regex rejects as non-digits. */
                                        dir="ltr"
                                        lang="en"
                                        value={inlineOtpDigits[idx]}
                                        onChange={(e) => onOtpDigitChange(idx, e.target.value)}
                                        onKeyDown={(e) => onOtpDigitKeyDown(idx, e)}
                                        disabled={loading}
                                        aria-label={`Digit ${idx + 1}`}
                                    />
                                ))}
                            </div>
                            {inlineOtpError && (
                                <div className={styles.otp4Error} role="alert">
                                    {inlineOtpError}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Phase 3E rev 3 — password moved ABOVE language. Single
                    field only; confirm-password dropped per founder spec.
                    The schema's confirmPassword equality check is satisfied
                    by mirroring the value via setValue3('step3ConfirmPassword'). */}
                <div className={styles.signupFields}>
                    <Controller
                        name="step3Password"
                        control={ctrl3}
                        render={({ field }) => (
                            <InputField
                                label={t('signup.passwordLabel')}
                                value={field.value ?? ''}
                                onChange={(v: string) => {
                                    field.onChange(v);
                                    // Mirror into confirmPassword to satisfy
                                    // signupStep3Schema's equality refine
                                    // without showing a second input.
                                    setValue3('step3ConfirmPassword', v, { shouldValidate: false });
                                }}
                                placeholder={t('signup.passwordPlaceholder')}
                                type="password"
                                name="signup-password"
                                autoComplete="new-password"
                                disabled={loading}
                                feedback={err3.step3Password ? 'error' : undefined}
                                feedbackMessage={mapPasswordError(err3.step3Password?.message)}
                            />
                        )}
                    />
                    {/* Phase 3E rev 4 — password complexity helper text shown
                        when the field is empty so users know what's required
                        (Capital + lowercase + digit + special char + 8 chars).
                        Hides once they start typing to give room for inline
                        validation feedback. */}
                    {!watchedPassword && (
                        <div className={styles.passwordHint}>
                            {t('signup.passwordComplexityHint')}
                        </div>
                    )}
                </div>

                {/* CR-2026-05-07a Phase 2B — Language + Time zone preferences. */}
                <div className={styles.prefRows}>
                    <EditPrefRow<'en' | 'ar'>
                        keyLabel={t('signup.prefs.languageKey')}
                        value={locale}
                        options={[
                            { value: 'en', label: 'English' },
                            { value: 'ar', label: 'العربية' },
                        ]}
                        onChange={onLanguageChange}
                        applyMode="instant"
                        editLabel={t('signup.prefs.edit')}
                        cancelLabel={t('signup.prefs.cancel')}
                        disabled={loading}
                    />
                    <EditPrefRow<string>
                        keyLabel={t('signup.prefs.timeZoneKey')}
                        value={tzPref}
                        /* Phase 3E rev 5 — keep both launch-market entries
                           visible AND surface whatever the country auto-
                           selected (so the displayed value isn't 'unknown'
                           for non-UAE/JO countries). Deduped via Map. */
                        options={Array.from(
                            new Map<string, { value: string; label: string; sublabel?: string }>([
                                ['Asia/Dubai', { value: 'Asia/Dubai', label: t('signup.prefs.tz.uae'), sublabel: 'GMT+4' }],
                                ['Asia/Amman', { value: 'Asia/Amman', label: t('signup.prefs.tz.jo'), sublabel: 'GMT+3' }],
                                [tzPref, { value: tzPref, label: tzPref.split('/').pop() ?? tzPref, sublabel: '' }],
                            ]).values(),
                        )}
                        onChange={setTzPref}
                        applyMode="confirm"
                        editLabel={t('signup.prefs.edit')}
                        cancelLabel={t('signup.prefs.cancel')}
                        saveLabel={t('signup.prefs.save')}
                        disabled={loading}
                    />
                </div>

                {/* CR-2026-05-07a Phase 3E rev 3 — single 'Create account'
                    button visible from the start. Disabled until the phone
                    is verified (the inline Verify link below the mobile
                    field is the path users take to satisfy this gate). */}
                <div className={styles.buttonWrpr}>
                    <Button
                        type="button"
                        label={loading ? t('signup.creatingAccount') : t('signup.createAccount')}
                        variant={loading || !phoneVerified ? 'disabled' : 'primary'}
                        disabled={loading || !phoneVerified}
                        onClick={() => submit3(onStep3Submit)()}
                    />
                </div>
                {!phoneVerified && (
                    <div className={styles.createAccountHint}>
                        {t('signup.otp.verifyHint')}
                    </div>
                )}

                {/* CR-2026-05-07a (rev 5 mockup) — horizontal divider before
                    the customer-app pitch + bottom legal/footer block. Visual
                    pattern matches Fresha's pro signup. Founder spec. */}
                <hr className={styles.signupDivider} aria-hidden="true" />

                <div className={styles.appPitchRow}>
                    <p className={styles.appPitchTitle}>{t('signup.customerCta.title')}</p>
                    <p className={styles.appPitchSub}>{t('signup.customerCta.sub')}</p>
                    <div className={styles.appBadgeRow}>
                        <a
                            className={styles.appBadge}
                            href={t('signup.customerCta.appStoreUrl')}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('signup.customerCta.appStoreAria')}
                        >
                            <svg className={styles.appBadgeIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M16.36 1c.06.97-.32 1.92-.96 2.6-.7.74-1.83 1.33-2.94 1.24-.07-.93.4-1.9.99-2.5C14.18 1.55 15.34.97 16.36 1zm3.5 17.05c-.51 1.18-.76 1.71-1.42 2.75-.92 1.45-2.21 3.25-3.81 3.27-1.43.01-1.79-.93-3.72-.92-1.93.01-2.33.94-3.76.92-1.6-.02-2.83-1.65-3.74-3.1C2 17.31.98 13.27 2.78 10.55c1.27-1.93 3.27-3.06 5.16-3.06 1.92 0 3.13 1.06 4.72 1.06 1.54 0 2.48-1.06 4.7-1.06 1.68 0 3.46.92 4.74 2.5-4.16 2.28-3.49 8.22-2.24 7.06z"/>
                            </svg>
                            <span className={styles.appBadgeStack}>
                                <span className={styles.appBadgeSmall}>{t('signup.customerCta.appStorePre')}</span>
                                <span className={styles.appBadgeBig}>{t('signup.customerCta.appStore')}</span>
                            </span>
                        </a>
                        <a
                            className={styles.appBadge}
                            href={t('signup.customerCta.playStoreUrl')}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('signup.customerCta.playStoreAria')}
                        >
                            <svg className={styles.appBadgeIcon} viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#34A853" d="M3 20.5V3.5c0-.6.3-1.1.8-1.4l10 8.4-10 10z"/>
                                <path fill="#FBBC04" d="M16.8 12.4l-3.4-2.9 2-1.7L18.5 9c.6.4.6 1.3 0 1.7l-1.7 1.7z"/>
                                <path fill="#EA4335" d="M3.8 2.1L13.4 10l2-1.7L4.5 2.1c-.2-.1-.5-.1-.7 0z"/>
                                <path fill="#4285F4" d="M3.8 21.9l9.6-7.9-2-1.7-7.6 7.6c-.3.4-.3.9 0 1.3v.7z"/>
                            </svg>
                            <span className={styles.appBadgeStack}>
                                <span className={styles.appBadgeSmall}>{t('signup.customerCta.playStorePre')}</span>
                                <span className={styles.appBadgeBig}>{t('signup.customerCta.playStore')}</span>
                            </span>
                        </a>
                    </div>
                </div>

                <p className={styles.passiveDisclosure} role="note">
                    {t('signup.passiveDisclosure.prefix')}{' '}
                    <a
                        href="/legal/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkBtn}
                    >
                        {t('signup.passiveDisclosure.terms')}
                    </a>
                    {' '}{t('signup.passiveDisclosure.connector')}{' '}
                    <a
                        href="/legal/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkBtn}
                    >
                        {t('signup.passiveDisclosure.privacy')}
                    </a>
                    {t('signup.passiveDisclosure.suffix')}
                </p>

                {/* CR-2026-05-07a Phase 3 — Sign-in link removed from
                    identifier step. It lives only on the email step (Step 1)
                    where existing users land first; once they pass email
                    they've committed to creating an account. */}
            </form>
        );
    }

    // ── Step 2 (OTP) ────────────────────────────────────────────────────────
    if (step === 'otp') {
        return (
            <form
                className={styles.signupForm}
                onSubmit={submit2(onStep2Submit)}
                aria-label={t('signup.step2Title')}
                autoComplete="off"
                noValidate
            >
                <div>
                    <p className={styles.signupText}>{t('signup.step2Title')}</p>
                    <p className={styles.signupSubtext}>
                        {t('signup.step2Subtitle', { identifier: normalizedIdentifier })}
                    </p>
                </div>

                {formError && (
                    <div className={styles.formError} role="alert" aria-live="polite">
                        <span aria-hidden="true">⚠</span> {formError}
                    </div>
                )}

                <div className={styles.signupFields}>
                    <Controller
                        name="code"
                        control={ctrl2}
                        render={({ field }) => (
                            <InputField
                                label={t('signup.otpLabel')}
                                // CR-2026-05-07 — guard `field.value` against undefined
                                // (mirrors the existing `|| ''` on email and `?? ''` on
                                // step3Password). Without this, the InputField default of
                                // `value = ''` does kick in, but the underlying RHF
                                // Controller can transiently emit undefined right after
                                // `reset2()` / step transitions — spelling the guard at
                                // the call site avoids subtle controlled→uncontrolled
                                // input flips that some browsers handle inconsistently.
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                placeholder={t('signup.otpPlaceholder')}
                                type="text"
                                name="code"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                disabled={loading}
                                feedback={err2.code ? 'error' : undefined}
                                feedbackMessage={err2.code?.message}
                            />
                        )}
                    />
                </div>

                <div className={styles.buttonWrpr}>
                    <Button
                        type="submit"
                        label={loading ? t('signup.verifyingCode') : t('signup.verifyCode')}
                        variant={loading ? 'disabled' : 'primary'}
                        disabled={loading}
                    />
                </div>

                <div className={styles.otpActions}>
                    <button
                        type="button"
                        onClick={handleResend}
                        className={styles.linkBtn}
                        disabled={loading || resendIn > 0}
                    >
                        {resendIn > 0
                            ? t('signup.resendIn', { seconds: resendIn })
                            : t('signup.resendCode')}
                    </button>
                    <span className={styles.separator} aria-hidden="true">·</span>
                    <button
                        type="button"
                        onClick={handleChangeIdentifier}
                        className={styles.linkBtn}
                        disabled={loading}
                    >
                        {t('signup.changeIdentifier')}
                    </button>
                </div>
            </form>
        );
    }

    // mapPasswordError / mapConfirmPasswordError moved earlier in the
    // component body (before the `if (step === 'identifier')` early-return)
    // to avoid the temporal-dead-zone ReferenceError when the merged
    // identifier step references them in its JSX. See top of component.

    // Phase 3E rev 16 — back from Step 3 (credentials) returns to Step 2
    // (identifier). Same browser-history pattern as the Step 2 back button.
    const onBackFromStep3 = () => {
        window.history.back();
    };

    return (
        <form
            className={styles.signupForm}
            onSubmit={submit3(onStep3Submit)}
            aria-label={t('signup.step3Title')}
            noValidate
            autoComplete="off"
        >
            {/* Phase 3E rev 16 — icon-only back, fixed top-start of viewport. */}
            <button
                type="button"
                className={styles.backLink}
                onClick={onBackFromStep3}
                aria-label={t('signup.back')}
                title={t('signup.back')}
            >
                <svg className={styles.backLinkIcon} viewBox="0 0 24 24" aria-hidden="true">
                    <path
                        d="M15 18l-6-6 6-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            <div>
                <p className={styles.signupText}>{t('signup.step3Title')}</p>
                <p className={styles.signupSubtext}>{t('signup.step3Subtitle')}</p>
            </div>

            {formError && (
                <div className={styles.formError} role="alert" aria-live="polite">
                    <span aria-hidden="true">⚠</span> {formError}
                </div>
            )}

            <div className={styles.signupFields}>
                {/* AB#1630 — Step 3 email field removed. Email is collected
                    once at Step 1 and carried via step1Values into the
                    register call. Step 3 is now password-capture only. */}
                <Controller
                    name="step3Password"
                    control={ctrl3}
                    render={({ field }) => (
                        <InputField
                            label={t('signup.passwordLabel')}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={t('signup.passwordPlaceholder')}
                            type="password"
                            name="signup-step3-password"
                            autoComplete="new-password"
                            disabled={loading}
                            feedback={err3.step3Password ? 'error' : undefined}
                            feedbackMessage={mapPasswordError(err3.step3Password?.message)}
                        />
                    )}
                />
                {watchedPassword.length > 0 && (
                    <div
                        className={`${styles.passwordStrength} ${styles[strengthToColor(passwordStrength.level)] ?? ''}`}
                        aria-live="polite"
                    >
                        <div
                            className={`${styles.passwordStrengthBar} ${styles[`bar_${strengthToColor(passwordStrength.level)}`] ?? ''}`}
                            data-strength={passwordStrength.score}
                        />
                        <span className={styles.passwordStrengthLabel}>
                            {t(`signup.passwordStrength.${strengthToColor(passwordStrength.level)}`)}
                        </span>
                    </div>
                )}
                <Controller
                    name="step3ConfirmPassword"
                    control={ctrl3}
                    render={({ field }) => (
                        <InputField
                            label={t('signup.confirmPasswordLabel')}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={t('signup.confirmPasswordPlaceholder')}
                            type="password"
                            name="signup-step3-confirm-password"
                            autoComplete="new-password"
                            disabled={loading}
                            feedback={err3.step3ConfirmPassword ? 'error' : undefined}
                            feedbackMessage={mapConfirmPasswordError(err3.step3ConfirmPassword?.message)}
                        />
                    )}
                />
            </div>

            <div className={styles.buttonWrpr}>
                <Button
                    type="submit"
                    label={loading ? t('signup.creatingAccount') : t('signup.createAccount')}
                    variant={loading ? 'disabled' : 'primary'}
                    disabled={loading}
                />
            </div>

            <div className={styles.otpActions}>
                <button
                    type="button"
                    onClick={handleChangeIdentifier}
                    className={styles.linkBtn}
                    disabled={loading}
                >
                    {t('signup.changeIdentifier')}
                </button>
            </div>
        </form>
    );
};

export default SignupForm;
